import React, {
    useEffect,
    useState,
    useRef
} from "react";

import axios from "axios";

import html2pdf from "html2pdf.js";

import {
    Download,
    FileText,
    TrendingUp,
    Target,
    Trophy,
    Activity,
    Brain,
    Eye,
    Zap,
    Clock
} from "lucide-react";

import IgniteNavbar from "../../layout/IgniteNavbar";
import IgniteHeader from "../../layout/IgniteHeader";

import "./IgniteReport.css";
import "./IgniteReportPdf.css";


/* =========================================================
   ASSESSMENT CONFIG
   -----------------------------------------
   Labels / icons only.
   Scores are NEVER hardcoded.
========================================================= */

const assessmentConfig = [

    {
        key: "visualAcuity",
        label: "Visual Acuity",
        icon: Eye,
        aliases: [
            "visualAcuity",
            "visual_acuity",
            "visual acuity"
        ]
    },

    {
        key: "contrastSensitivity",
        label: "Contrast Sensitivity",
        icon: Target,
        aliases: [
            "contrastSensitivity",
            "contrast_sensitivity",
            "contrast sensitivity"
        ]
    },

    {
        key: "simpleReaction",
        label: "Simple Reaction",
        icon: Zap,
        aliases: [
            "simpleReaction",
            "simple_reaction",
            "simple reaction",
            "reactionSpeed",
            "reaction_speed"
        ]
    },

    {
        key: "complexReaction",
        label: "Complex Reaction",
        icon: Activity,
        aliases: [
            "complexReaction",
            "complex_reaction",
            "complex reaction"
        ]
    },



    {
        key: "shortTermMemory",
        label: "Short-Term Memory",
        icon: Brain,
        aliases: [
            "shortTermMemory",
            "short_term_memory",
            "short-term memory",
            "short memory"
        ]
    },

    {
    key: "workingMemory",
    label: "Working Memory",
    icon: Brain,
    aliases: [
        "workingMemory",
        "working_memory",
        "working memory",
        "memoryLevel"
    ]
},

    {
        key: "searchSpan",
        label: "Search Span",
        icon: Eye,
        aliases: [
            "searchSpan",
            "search_span",
            "search span"
        ]
    },

    {
    key: "anticipation",
    label: "Anticipation",
    icon: Target,
    aliases: [
        "anticipation",
        "anticipationTest",
        "anticipation_test",
        "anticipationScore"
    ]
},
];


/* =========================================================
   PDF CATEGORY GROUPING
   (matches ReportPage.jsx layout: orange / blue / green)
========================================================= */

const basicKeys = [
    "visualAcuity",
    "contrastSensitivity"
];

const processingKeys = [
    "simpleReaction",
    "complexReaction"
];

const executiveKeys = [
    "shortTermMemory",
    "workingMemory",
    "searchSpan",
    "anticipation"
];

const definitionText = {

    visualAcuity: {
        what: "How clearly one can see small details at 6 m (both eyes).",
        read: "The higher the better (6/6 = 1.0; 6/4.5 ≈ 1.33).",
        why: "Sharp visual clarity supports faster reactions, accurate targeting, and precise execution under pressure. It’s the foundation for reading the game early, coordinating efficiently."
    },

    contrastSensitivity: {
        what: "Ability to detect objects against low contrast backgrounds.",
        read: "Lower value indicates better sensitivity (can detect finer contrast differences).",
        why: "Critical in low light, fog, or fast-moving environments where clear visibility is reduced. Helps in early detection of objects, opponents, and movement."
    },

    simpleReaction: {
        what: "Time taken to respond to a sudden light cue.",
        read: "Reported in milliseconds. Lower score implies quicker reactions.",
        why: "Supports paying attention to cues, timely starts, and fewer slip-ups."
    },

    complexReaction: {
        what: "How quickly an athlete reacts to unpredictable or multi-choice stimuli.",
        read: "Reported in milliseconds. Lower score implies quicker reactions.",
        why: "Reflects processing speed, focus, and adaptability under pressure. Essential in fast-paced sports where milliseconds decide performance."
    },

    shortTermMemory: {
        what: "How well one remembers numbers and recites them back in order.",
        read: "Longest number string said correctly. Higher implies stronger short-term memory.",
        why: "Supports strategy recall, quick calculations, and multi-step execution."
    },

    workingMemory: {
        what: "How well one remembers numbers and recites them back in order.",
        read: "Longest number string said correctly. Higher implies stronger short-term memory.",
        why: "Supports strategy recall, quick calculations, and multi-step execution."
    },

    searchSpan: {
        what: "How fast one can find the right items when the clock is ticking.",
        read: "More correct finds implies better search-and-find ability.",
        why: "Key for reading the game, spotting opportunities, and reacting fast."
    },

    anticipation: {
        what: "Anticipation of motion.",
        read: "Percent correct, higher implies better prediction and timing.",
        why: "Crucial for reading motion and reacting early, from catching and intercepting to crossing, passing, or positioning accurately in dynamic environments."
    }

};


/* =========================================================
   NORMALIZE KEY
========================================================= */

const normalizeKey = (
    value
) => {

    return String(value || "")
        .toLowerCase()
        .replace(
            /[^a-z0-9]/g,
            ""
        );

};


/* =========================================================
   GET RAW METRICS
========================================================= */

const getRawMetrics = (
    report
) => {

    if (
        Array.isArray(
            report?.assessments
        )
    ) {

        return report.assessments;

    }

    if (
        Array.isArray(
            report?.results
        )
    ) {

        return report.results;

    }

    if (
        Array.isArray(
            report?.metrics
        )
    ) {

        return report.metrics;

    }

    return (
        report?.metrics ||
        report?.assessments ||
        report?.results ||
        {}
    );

};


/* =========================================================
   FIND ASSESSMENT DATA
========================================================= */

const findAssessmentData = (
    rawMetrics,
    config
) => {

    /* -----------------------------------------
       ARRAY RESPONSE
    ----------------------------------------- */

    if (
        Array.isArray(rawMetrics)
    ) {

        const found =
            rawMetrics.find(
                item => {

                    const name =
                        item?.key ||
                        item?.name ||
                        item?.test ||
                        item?.assessment ||
                        item?.title ||
                        "";

                    const normalized =
                        normalizeKey(
                            name
                        );

                    return config.aliases.some(
                        alias =>
                            normalizeKey(
                                alias
                            ) === normalized
                    );

                }
            );

        return found || null;

    }


    /* -----------------------------------------
       OBJECT RESPONSE
    ----------------------------------------- */

    if (
        rawMetrics &&
        typeof rawMetrics === "object"
    ) {

        const entries =
            Object.entries(
                rawMetrics
            );

        for (
            const [
                key,
                value
            ]
            of entries
        ) {

            const normalized =
                normalizeKey(
                    key
                );

            const matched =
                config.aliases.some(
                    alias =>
                        normalizeKey(
                            alias
                        ) === normalized
                );

            if (matched) {

                return value;

            }

        }

    }

    return null;

};


/* =========================================================
   GET SCORE
========================================================= */

const getScore = (
    item
) => {

    if (
        item === null ||
        item === undefined
    ) {

        return null;

    }


    if (
        typeof item === "number"
    ) {

        return item;

    }


    if (
        typeof item === "string"
    ) {

        const number =
            Number(
                item
            );

        return Number.isFinite(
            number
        )
            ? number
            : null;

    }


    if (
        typeof item === "object"
    ) {

        const possibleValues = [

            item.percentile,

            item.score,

            item.value,

            item.result,

            item.percentage,

            item.finalNumber,

            item.accuracy

        ];


        for (
            const value
            of possibleValues
        ) {

            if (
                value !== null &&
                value !== undefined &&
                value !== ""
            ) {

                const number =
                    Number(
                        value
                    );

                if (
                    Number.isFinite(
                        number
                    )
                ) {

                    return number;

                }

            }

        }

    }

    return null;

};

/* =========================================================
   GET ACTUAL VALUE
========================================================= */

const getActualValue = (item) => {

    if (
        item === null ||
        item === undefined
    ) {
        return null;
    }

    if (typeof item === "number") {
        return item;
    }

    if (typeof item === "string") {
        return item;
    }

    if (typeof item === "object") {

        const values = [

            item.value,

            item.result,

            item.finalNumber,

            item.actualValue,

            item.rawValue,

            item.measurement

        ];

        for (const value of values) {

            if (
                value !== null &&
                value !== undefined &&
                value !== ""
            ) {
                return value;
            }

        }
    }

    return null;
};


/* =========================================================
   FORMAT ACTUAL VALUE
========================================================= */

const formatActualValue = (item) => {

    const value =
        getActualValue(item?.data);

    if (
        value === null ||
        value === undefined
    ) {
        return "-";
    }

    if (typeof value === "number") {
        return value.toFixed(2);
    }

    return value;
};


/* =========================================================
   GET PERCENTILE
========================================================= */

const getPercentile = (item) => {

    if (!item?.data) {
        return null;
    }

    const percentileValues = [

        item.data.percentile,

        item.data.percentileScore,

        item.data.percentileValue,

        item.data.percentileRank

    ];

    for (
        const value
        of percentileValues
    ) {

        if (
            value !== null &&
            value !== undefined &&
            value !== ""
        ) {

            const number =
                Number(value);

            if (
                Number.isFinite(number)
            ) {
                return number;
            }

        }

    }

    return null;
};
/* =========================================================
   REPORT PDF
   -----------------------------------------
   Rebuilt to mirror ReportPage.jsx exactly:
   logo header, colored category table
   (orange / blue / green), testing pyramid,
   page-break, and a second page with full
   test definitions.
========================================================= */

const ReportPdf = React.forwardRef(
    ({
        report
    }, ref) => {

        const rawMetrics =
            getRawMetrics(
                report
            );


        const allAssessments =
            assessmentConfig.map(
                config => {

                    const data =
                        findAssessmentData(
                            rawMetrics,
                            config
                        );

                    const score =
                        getScore(
                            data
                        );

                    return {

                        ...config,

                        data,

                        score,

                        available:
                            score !== null

                    };

                }
            );


        const getByKey = (key) =>
            allAssessments.find(
                (item) => item.key === key
            );


        const displayValue = (item) => {

            if (!item || !item.available) {
                return "-";
            }

            const raw =
                item.data?.value ??
                item.data?.result ??
                item.data?.score ??
                item.score;

            return typeof raw === "number"
                ? raw.toFixed(2)
                : raw;

        };


        const displayPercentile = (item) =>
            item && item.available
                ? item.score.toFixed(2)
                : "-";


        const basicRows =
            basicKeys
                .map(getByKey)
                .filter((item) => item && item.available);

        const processingRows =
            processingKeys
                .map(getByKey)
                .filter((item) => item && item.available);

        const executiveRows =
            executiveKeys
                .map(getByKey)
                .filter((item) => item && item.available);


        return (

            <div
                ref={ref}
                className="report"
            >

                {/* HEADER */}
                <div className="headerRow">

                    <div className="logo">

                        <img
                            src="/Logo.png"
                            alt="Forge Your Peak"
                            className="brand-logo"
                        />

                    </div>


                    <div className="company">

                        <h2>
                            Forge Your Peak Pvt Ltd
                        </h2>

                        <p>
                            Assessment Report
                        </p>

                    </div>


                    <div className="contact">

                        <p>support@forgeyourpeak.in</p>
                        <p>+91 8056023798</p>
                        <p>https://forgeyourpeak.in/</p>

                    </div>

                </div>


                {/* INFO TABLE */}
                <table className="infoTable">

                    <tbody>

                        <tr>

                            <td>
                                Name: {
                                    report?.name ||
                                    report?.athleteName ||
                                    "-"
                                }
                            </td>

                            <td>
                                Athlete Id: {
                                    report?.athleteId ||
                                    "-"
                                }
                            </td>

                        </tr>


                        <tr>

                            <td>
                                Email: {
                                    report?.email ||
                                    "-"
                                }
                            </td>

                            <td>
                                Report Id: {
                                    report?.reportId ||
                                    "-"
                                }
                            </td>

                        </tr>


                        <tr>

                            <td>
                                Test Type: {
                                    report?.testType ||
                                    "Vision Ignite"
                                }
                            </td>

                            <td>
                                Collection Date: {
                                    report?.collectionDate ||
                                    report?.date ||
                                    report?.createdAt ||
                                    new Date().toLocaleDateString()
                                }
                            </td>

                        </tr>

                    </tbody>

                </table>


                {/* MAIN TABLE */}
                <table className="mainTable">

                    <thead>

                        <tr>
                            <th>Metric</th>
                            <th>Sub-Metric</th>
                            <th>Value</th>
                            <th>Percentile</th>
                        </tr>

                    </thead>


                    <tbody>

                        {/* BASIC */}
                        {basicRows.length > 0 &&
                            basicRows.map((row, i) => (
                                <tr key={row.key}>
                                    {i === 0 && (
                                        <td
                                            className="orange"
                                            rowSpan={basicRows.length}
                                        >
                                            Basic Visual Functions
                                        </td>
                                    )}
                                    <td>{row.label}</td>
                                    <td>{displayValue(row)}</td>
                                    <td>{displayPercentile(row)}</td>
                                </tr>
                            ))}

                        {/* PROCESSING */}
                        {processingRows.length > 0 &&
                            processingRows.map((row, i) => (
                                <tr key={row.key}>
                                    {i === 0 && (
                                        <td
                                            className="blue"
                                            rowSpan={processingRows.length}
                                        >
                                            Visual Processing Speed
                                        </td>
                                    )}
                                    <td>{row.label}</td>
                                    <td>{displayValue(row)}</td>
                                    <td>{displayPercentile(row)}</td>
                                </tr>
                            ))}

                        {/* EXECUTIVE */}
                        {executiveRows.length > 0 &&
                            executiveRows.map((row, i) => (
                                <tr key={row.key}>
                                    {i === 0 && (
                                        <td
                                            className="green"
                                            rowSpan={executiveRows.length}
                                        >
                                            Visual Executive Functions
                                        </td>
                                    )}
                                    <td>{row.label}</td>
                                    <td>{displayValue(row)}</td>
                                    <td>{displayPercentile(row)}</td>
                                </tr>
                            ))}

                    </tbody>

                </table>


                {/* PYRAMID */}
                <div className="pyramidSection">

                    <h3>Testing Pyramid</h3>

                    <div className="pyramidWrap">

                        <div className="pyramid">
                            <div className="layer top">Visual Executive Functions</div>
                            <div className="layer middle">Visual Processing Speed</div>
                            <div className="layer bottom">Basic Visual Functions</div>
                        </div>

                        <div className="desc">

                            <div className="descItem greenLine">
                                <h4>Visual Executive Functions</h4>
                                <p>Attention, working memory, anticipation</p>
                            </div>

                            <div className="descItem blueLine">
                                <h4>Visual Processing Speed</h4>
                                <p>Reaction speed</p>
                            </div>

                            <div className="descItem orangeLine">
                                <h4>Basic Visual Functions</h4>
                                <p>Clarity & contrast</p>
                            </div>

                        </div>

                    </div>

                </div>

                <div className="page-break"></div>


                {/* SECOND PAGE — DEFINITIONS */}
                <div className="secondPage">

                    <div className="pdfTopSpace"></div>

                    <h2 className="title">Test Definitions & Details</h2>

                    <table className="bigTable">

                        <thead>
                            <tr>
                                <th>Metric</th>
                                <th>Sub-Metric</th>
                                <th>Definition and Details</th>
                            </tr>
                        </thead>

                        <tbody>

                            {/* BASIC */}
                            {basicRows.map((row, i) => (
                                <tr key={row.key}>
                                    {i === 0 && (
                                        <td
                                            className="orangeMain"
                                            rowSpan={basicRows.length}
                                        >
                                            Basic<br />Visual<br />Functions
                                        </td>
                                    )}
                                    <td className="orangeSub">{row.label}</td>
                                    <td>
                                        <b>What it measures:</b>{" "}
                                        {definitionText[row.key]?.what}
                                        <br />
                                        <b>How to read:</b>{" "}
                                        {definitionText[row.key]?.read}
                                        <br />
                                        <b>Why it matters:</b>{" "}
                                        {definitionText[row.key]?.why}
                                    </td>
                                </tr>
                            ))}

                            {/* PROCESSING */}
                            {processingRows.map((row, i) => (
                                <tr key={row.key}>
                                    {i === 0 && (
                                        <td
                                            className="blueMain"
                                            rowSpan={processingRows.length}
                                        >
                                            Visual<br />Processing<br />Speed
                                        </td>
                                    )}
                                    <td className="blueSub">{row.label}</td>
                                    <td>
                                        <b>What it measures:</b>{" "}
                                        {definitionText[row.key]?.what}
                                        <br />
                                        <b>How to read:</b>{" "}
                                        {definitionText[row.key]?.read}
                                        <br />
                                        <b>Why it matters:</b>{" "}
                                        {definitionText[row.key]?.why}
                                    </td>
                                </tr>
                            ))}

                            {/* EXECUTIVE */}
                            {executiveRows.map((row, i) => (
                                <tr key={row.key}>
                                    {i === 0 && (
                                        <td
                                            className="greenMain"
                                            rowSpan={executiveRows.length}
                                        >
                                            Visual<br />Executive<br />Functions
                                        </td>
                                    )}
                                    <td className="greenSub">{row.label}</td>
                                    <td>
                                        <b>What it measures:</b>{" "}
                                        {definitionText[row.key]?.what}
                                        <br />
                                        <b>How to read:</b>{" "}
                                        {definitionText[row.key]?.read}
                                        <br />
                                        <b>Why it matters:</b>{" "}
                                        {definitionText[row.key]?.why}
                                    </td>
                                </tr>
                            ))}

                        </tbody>

                    </table>

                </div>

            </div>

        );

    }
);

ReportPdf.displayName =
    "ReportPdf";


/* =========================================================
   MAIN COMPONENT
========================================================= */

const IgniteReport = () => {
     

    const email =
        localStorage.getItem(
            "user_email"
        );
    useEffect(() => {
            const navbar = document.querySelector("header, .navbar");
            const footer = document.querySelector("footer");
          
           
            if (navbar) navbar.style.display = "none";
            if (footer) footer.style.display = "none";
          
            return () => {
              // 🔥 மீண்டும் restore
              if (navbar) navbar.style.display = "";
              if (footer) footer.style.display = "";
            };
          }, []);

    const [
        report,
        setReport
    ] = useState(null);


    const [
        loading,
        setLoading
    ] = useState(true);


    const [
        exporting,
        setExporting
    ] = useState(false);


    const [
        error,
        setError
    ] = useState("");


    const [
        showAll,
        setShowAll
    ] = useState(false);


    const pdfRef =
        useRef(null);


    /* =====================================================
       FETCH REPORT
    ===================================================== */

    useEffect(() => {

        if (!email) {

            setError(
                "User email not found."
            );

            setLoading(false);

            return;

        }


        fetchReport();

    }, [email]);


    const fetchReport =
        async () => {

            try {

                setLoading(true);

                setError("");


                /*
                 * Use your existing
                 * report API.
                 */

                const response =
                    await axios.get(
                        `/get-report?email=${encodeURIComponent(
                            email
                        )}`
                    );


                setReport(
                    response.data
                );

            }

            catch (err) {

                console.error(
                    "Report Error:",
                    err
                );

                setError(
                    err.response?.data?.message ||
                    "Unable to load report."
                );

            }

            finally {

                setLoading(false);

            }

        };


    /* =====================================================
       RAW METRICS
    ===================================================== */

    const rawMetrics =
        getRawMetrics(
            report
        );


    /* =====================================================
       BUILD ALL 9 TESTS FROM API
    ===================================================== */

    const allAssessments =
        assessmentConfig.map(
            config => {

                const data =
                    findAssessmentData(
                        rawMetrics,
                        config
                    );

                const score =
                    getScore(
                        data
                    );


                return {

                    ...config,

                    data,

                    score,

                    available:
                        score !== null

                };

            }
        );


    /* =====================================================
       AVAILABLE TESTS
    ===================================================== */

    const availableAssessments =
        allAssessments.filter(
            item =>
                item.available
        );


    /* =====================================================
       DISPLAY TESTS
       -----------------------------------------
       Initial: first 7
       View All: all 9
    ===================================================== */

    const visibleAssessments =
        showAll
            ? allAssessments
            : availableAssessments.slice(
                0,
                7
            );


    /* =====================================================
       AVERAGE
    ===================================================== */

    const averagePercentile =
        availableAssessments.length
            ? Math.round(
                availableAssessments.reduce(
                    (
                        total,
                        item
                    ) =>
                        total +
                        item.score,
                    0
                ) /
                availableAssessments.length
            )
            : 0;


    /* =====================================================
       EXPORT
    ===================================================== */

    const handleExportReport =
        async () => {

            if (
                exporting ||
                !email
            ) {

                return;

            }


            try {

                setExporting(true);

                setError("");


                /*
                 * Get latest report
                 */

                const response =
                    await axios.get(
                        `/get-report?email=${encodeURIComponent(
                            email
                        )}`
                    );


                const freshReport =
                    response.data;


                if (
                    !freshReport
                ) {

                    throw new Error(
                        "Report data not available."
                    );

                }


                setReport(
                    freshReport
                );


                /*
                 * Allow PDF DOM
                 * to render
                 */

                await new Promise(
                    resolve =>
                        requestAnimationFrame(
                            () =>
                                requestAnimationFrame(
                                    resolve
                                )
                        )
                );


                if (
                    !pdfRef.current
                ) {

                    throw new Error(
                        "Unable to prepare report."
                    );

                }


                const athleteName =
                    freshReport.name ||
                    freshReport.athleteName ||
                    "Athlete";


                const safeName =
                    athleteName
                        .replace(
                            /[^a-z0-9]/gi,
                            "_"
                        );


                const options = {

                    margin: 0,

                    filename:
                        `${safeName}_Performance_Report.pdf`,

                    image: {

                        type: "jpeg",

                        quality: 0.98

                    },

                    html2canvas: {

                        scale: 2,

                        useCORS: true,

                        scrollY: 0

                    },

                    jsPDF: {

                        unit: "mm",

                        format: "a4",

                        orientation:
                            "portrait"

                    },

                    pagebreak: {

                        mode: [
                            "css",
                            "legacy"
                        ]

                    }

                };


                /*
                 * DOWNLOAD
                 */

                await html2pdf()
                    .set(options)
                    .from(
                        pdfRef.current
                    )
                    .save();


                /*
                 * AFTER SUCCESSFUL
                 * DOWNLOAD
                 */

                try {

                    await axios.post(
                        "/track-download",
                        {

                            email:
                                freshReport.email ||
                                email,

                            reportId:
                                freshReport.reportId ||
                                "",

                            action:
                                "download",

                            downloadedAt:
                                new Date()

                        }
                    );

                }

                catch (
                    trackingError
                ) {

                    console.error(
                        "Download tracking failed:",
                        trackingError
                    );

                }

            }

            catch (err) {

                console.error(
                    "Export Error:",
                    err
                );

                setError(
                    err.response?.data?.message ||
                    err.message ||
                    "Unable to download report."
                );

            }

            finally {

                setExporting(false);

            }

        };


    /* =====================================================
       LOADING
    ===================================================== */

    if (loading) {

        return (

            <div className="ignite-reports">

                <IgniteNavbar />

                <main className="ignite-reports-main">

                    <IgniteHeader
                        title="Reports"
                        subtitle="Track your performance, progress, and achievements."
                    />

                    <div className="loadingText">

                        Loading report...

                    </div>

                </main>

            </div>

        );

    }


    /* =====================================================
       PAGE
    ===================================================== */

    return (

        <div className="ignite-reports">


            {/* ================================================
                SAME NAVBAR AS DASHBOARD
            ================================================ */}

            <IgniteNavbar />


            <main className="ignite-reports-main">


                {/* ============================================
                    SAME HEADER AS DASHBOARD
                ============================================ */}

                <IgniteHeader
                    title="Reports"
                    subtitle="Track your performance, progress, and achievements."
                />


                {/* ============================================
                    ACTION
                ============================================ */}

                <div className="actionsRow">

                    <button
                        className="exportBtn"
                        onClick={
                            handleExportReport
                        }
                        disabled={
                            exporting ||
                            !report
                        }
                    >

                        <Download
                            size={16}
                        />

                        {
                            exporting
                                ? "Preparing..."
                                : "Export Report"
                        }

                    </button>

                </div>


                {error && (

                    <div className="errorBanner">

                        {error}

                    </div>

                )}


                {/* ============================================
                    STAT CARDS
                ============================================ */}

                <div className="statGrid">


                    <div className="statCard">

                        <div
                            className="statIcon"
                            style={{
                                background:
                                    "#eaf0ff",
                                color:
                                    "#2f6fef"
                            }}
                        >

                            <Activity
                                size={20}
                            />

                        </div>

                        <div>

                            <div className="statLabel">
                                Assessments
                            </div>

                            <div className="statValue">
                                {
                                    availableAssessments.length
                                }
                            </div>

                            <div className="statSub">
                                Metrics available
                            </div>

                        </div>

                    </div>


                    <div className="statCard">

                        <div
                            className="statIcon"
                            style={{
                                background:
                                    "#e6f7ef",
                                color:
                                    "#22b07d"
                            }}
                        >

                            <Target
                                size={20}
                            />

                        </div>

                        <div>

                            <div className="statLabel">
                                Average Percentile
                            </div>

                            <div className="statValue">
                                {averagePercentile}%
                            </div>

                            <div className="statSub">
                                Overall performance
                            </div>

                        </div>

                    </div>


                    <div className="statCard">

                        <div
                            className="statIcon"
                            style={{
                                background:
                                    "#f0e9ff",
                                color:
                                    "#7657d9"
                            }}
                        >

                            <FileText
                                size={20}
                            />

                        </div>

                        <div>

                            <div className="statLabel">
                                Report ID
                            </div>

                            <div className="statValue">
                                {
                                    report?.reportId ||
                                    "-"
                                }
                            </div>

                            <div className="statSub">
                                Performance record
                            </div>

                        </div>

                    </div>


                    <div className="statCard">

                        <div
                            className="statIcon"
                            style={{
                                background:
                                    "#fff1dc",
                                color:
                                    "#f5a623"
                            }}
                        >

                            <Trophy
                                size={20}
                            />

                        </div>

                        <div>

                            <div className="statLabel">
                                Athlete
                            </div>

                            <div className="statValue">
                                {
                                    report?.name ||
                                    report?.athleteName ||
                                    "-"
                                }
                            </div>

                            <div className="statSub">
                                {
                                    report?.athleteId ||
                                    ""
                                }
                            </div>

                        </div>

                    </div>

                </div>


                {/* ============================================
                    PERFORMANCE / CATEGORY
                ============================================ */}

                <div className="chartsGrid">


                    {/* PERFORMANCE */}

                    <section className="panel">

                        <div
                            className="panelHeader"
                        >

                            <div>

                                <h3>
                                    Performance Overview
                                </h3>

                                <p>
                                    Your latest assessment results.
                                </p>

                            </div>


                            <button
                                className="viewAllBtn"
                                onClick={() =>
                                    setShowAll(
                                        !showAll
                                    )
                                }
                            >

                                {
                                    showAll
                                        ? "Show Less"
                                        : "View All"
                                }

                            </button>

                        </div>


                        <div className="performanceList">

                            {visibleAssessments.map(
                                item => {

                                    const Icon =
                                        item.icon;


                                    return (

                                        <div
                                            className="performanceItem"
                                            key={
                                                item.key
                                            }
                                        >

                                            <div className="performanceTop">

                                                <span>

                                                    <Icon
                                                        size={15}
                                                        color="#2f6fef"
                                                    />

                                                    {
                                                        item.label
                                                    }

                                                </span>


                                                <strong>

                                                    {
                                                        item.available
                                                            ? item.score.toFixed(2)
                                                            : "Not completed"
                                                    }

                                                </strong>

                                            </div>


                                            <div className="performanceBar">

                                                <div
                                                    className="performanceFill"
                                                    style={{
                                                        width:
                                                            item.available
                                                                ? `${Math.min(
                                                                    Math.max(
                                                                        item.score,
                                                                        0
                                                                    ),
                                                                    100
                                                                )}%`
                                                                : "0%"
                                                    }}
                                                />

                                            </div>

                                        </div>

                                    );

                                }
                            )}

                        </div>

                    </section>


                    {/* CATEGORY */}

                    <section className="panel">

                        <div className="panelHeader">

                            <div>

                                <h3>
                                    Performance by Category
                                </h3>

                                <p>
                                    Assessment distribution.
                                </p>

                            </div>

                        </div>


                        <div className="categoryList">


                            <div className="categoryCard">

                                <div className="categoryIcon">

                                    <Eye
                                        size={18}
                                    />

                                </div>

                                <div>

                                    <strong>
                                        Basic Visual Functions
                                    </strong>

                                    <span>
                                        {
                                            availableAssessments.filter(
                                                item =>
                                                    [
                                                        "visualAcuity",
                                                        "contrastSensitivity"
                                                    ].includes(
                                                        item.key
                                                    )
                                            ).length
                                        }
                                        {" "}
                                        metrics
                                    </span>

                                </div>

                            </div>


                            <div className="categoryCard">

                                <div className="categoryIcon">

                                    <Zap
                                        size={18}
                                    />

                                </div>

                                <div>

                                    <strong>
                                        Visual Processing Speed
                                    </strong>

                                    <span>
                                        {
                                            availableAssessments.filter(
                                                item =>
                                                    [
                                                        "simpleReaction",
                                                        "complexReaction"
                                                    ].includes(
                                                        item.key
                                                    )
                                            ).length
                                        }
                                        {" "}
                                        metrics
                                    </span>

                                </div>

                            </div>


                            <div className="categoryCard">

                                <div className="categoryIcon">

                                    <Brain
                                        size={18}
                                    />

                                </div>

                                <div>

                                    <strong>
                                        Visual Executive Functions
                                    </strong>

                                    <span>
                                        {
                                            availableAssessments.filter(
                                                item =>
                                                    [
                                                        "handEyeCoordination",
                                                        "shortTermMemory",
                                                        "workingMemory",
                                                        "searchSpan",
                                                        "anticipation"
                                                    ].includes(
                                                        item.key
                                                    )
                                            ).length
                                        }
                                        {" "}
                                        metrics
                                    </span>

                                </div>

                            </div>

                        </div>

                    </section>

                </div>


                {/* ============================================
                    RECENT ASSESSMENTS
                ============================================ */}

                <div className="bottomGrid">


                    <section className="panel">

                        <div className="panelHeader">

                            <div>

                                <h3>
                                    Recent Assessments
                                </h3>

                                <p>
                                    Detailed assessment results.
                                </p>

                            </div>


                            <button
                                className="viewAllBtn"
                                onClick={() =>
                                    setShowAll(
                                        !showAll
                                    )
                                }
                            >

                                {
                                    showAll
                                        ? "Show Less"
                                        : "View All"
                                }

                            </button>

                        </div>


                        <div className="tableScroll">

                            <table className="assessmentsTable">

                                <thead>

                                    <tr>

                                        <th>
                                            Assessment
                                        </th>

                                        <th>
                                            Value
                                        </th>

                                        <th>
                                            Percentile
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

    {visibleAssessments.map(
        item => {

            const Icon =
                item.icon;

            const actualValue =
                formatActualValue(item);

            const percentile =
                getPercentile(item);

            return (

                <tr
                    key={item.key}
                >

                    {/* =========================
                        ASSESSMENT
                    ========================= */}

                    <td>

                        <div className="xpCell">

                            <Icon
                                size={15}
                                color="#2f6fef"
                            />

                            {item.label}

                        </div>

                    </td>


                    {/* =========================
                        ACTUAL VALUE
                    ========================= */}

                    <td>

                        {
                            item.available
                                ? actualValue
                                : "-"
                        }

                    </td>


                    {/* =========================
                        PERCENTILE
                    ========================= */}

                    <td>

                        <span
                            className={
                                percentile !== null
                                    ? "ratingPill good"
                                    : "ratingPill neutral"
                            }
                        >

                            {
                                percentile !== null
                                    ? percentile.toFixed(2)
                                    : "-"
                            }

                        </span>

                    </td>

                </tr>

            );

        }
    )}

</tbody>
                            </table>

                        </div>

                    </section>


                    {/* ========================================
                        INSIGHTS
                    ======================================== */}

                    <section className="panel">

                        <div className="panelHeader">

                            <div>

                                <h3>
                                    Insights & Summary
                                </h3>

                                <p>
                                    Your current performance summary.
                                </p>

                            </div>

                        </div>


                        <ul className="insightList">


                            <li className="insightItem">

                                <div className="insightIcon tone-trend">

                                    <TrendingUp
                                        size={16}
                                    />

                                </div>

                                <div>

                                    <p className="insightTitle">
                                        Assessment Coverage
                                    </p>

                                    <p className="insightBody">

                                        {
                                            availableAssessments.length
                                        }
                                        {" "}
                                        assessment metrics
                                        are available.

                                    </p>

                                </div>

                            </li>


                            <li className="insightItem">

                                <div className="insightIcon tone-timer">

                                    <Clock
                                        size={16}
                                    />

                                </div>

                                <div>

                                    <p className="insightTitle">
                                        Average Performance
                                    </p>

                                    <p className="insightBody">

                                        Current average:
                                        {" "}
                                        {
                                            averagePercentile
                                        }%.

                                    </p>

                                </div>

                            </li>


                            <li className="insightItem">

                                <div className="insightIcon tone-trophy">

                                    <Trophy
                                        size={16}
                                    />

                                </div>

                                <div>

                                    <p className="insightTitle">
                                        Report Ready
                                    </p>

                                    <p className="insightBody">

                                        Your performance
                                        report is ready
                                        to export.

                                    </p>

                                </div>

                            </li>


                        </ul>

                    </section>

                </div>

            </main>


            {/* ================================================
                HIDDEN PDF
            ================================================ */}

            <div
                className="hiddenExportTarget"
                aria-hidden="true"
            >

                {report && (

                    <ReportPdf
                        report={report}
                        ref={pdfRef}
                    />

                )}

            </div>

        </div>

    );

};


export default IgniteReport;
